/**
 * @file Borradores bilingües y foto en `localStorage`.
 * @module cvStorage
 */

/** Clave del JSON (`version`, `locales`, `profileImage` opcional). */
export const CV_DATA_STORAGE_KEY = "wetag-cv-data";

const PROFILE_IMAGE_KEY = "profileImage";

let persistTimer = 0;

/**
 * @typedef {{
 *   version: 1,
 *   profileImage?: string,
 *   locales: { en: Record<string, unknown>, es: Record<string, unknown> }
 * }} BilingualStore
 */

/**
 * @returns {BilingualStore}
 */
function emptyStore() {
	return {
		version: 1,
		locales: { en: {}, es: {} },
	};
}

/**
 * Separa `profileImage` del resto del objeto plano del formulario.
 * @param {Record<string, unknown>} data
 * @returns {{ rest: Record<string, unknown>, profileImage: string }} `profileImage` vacío si no es `data:` URL.
 */
function splitProfileImage(data) {
	const rest = { ...data };
	const raw = rest[PROFILE_IMAGE_KEY];
	delete rest[PROFILE_IMAGE_KEY];
	const profileImage =
		typeof raw === "string" && raw.startsWith("data:") ? raw : "";
	return { rest, profileImage };
}

/**
 * Escribe en memoria del store el locale `L` y la foto compartida.
 * @param {BilingualStore} s
 * @param {"en" | "es"} L
 * @param {() => Record<string, unknown>} readData
 */
export function persistFormIntoStore(s, L, readData) {
	const data = readData();
	const { rest, profileImage } = splitProfileImage(data);
	if (profileImage) {
		s.profileImage = profileImage;
	} else {
		delete s.profileImage;
	}
	s.locales[L] = rest;
}

/**
 * @returns {BilingualStore}
 */
export function readBilingualStore() {
	const raw = localStorage.getItem(CV_DATA_STORAGE_KEY);
	if (!raw) return emptyStore();
	try {
		const o = JSON.parse(raw);
		if (
			o &&
			o.version === 1 &&
			o.locales &&
			typeof o.locales === "object" &&
			typeof o.locales.en === "object" &&
			typeof o.locales.es === "object"
		) {
			return o;
		}
	} catch {
		/* ignore */
	}
	return emptyStore();
}

/**
 * @param {BilingualStore} store
 */
export function writeBilingualStore(store) {
	localStorage.setItem(CV_DATA_STORAGE_KEY, JSON.stringify(store));
}

/**
 * @param {"en" | "es"} loc
 */
export function loadLocaleData(loc) {
	const s = readBilingualStore();
	const chunk = s.locales[loc];
	const base =
		chunk && typeof chunk === "object" && !Array.isArray(chunk) ? { ...chunk } : {};
	const shared =
		typeof s.profileImage === "string" && s.profileImage.startsWith("data:")
			? s.profileImage
			: "";
	if (shared) {
		base[PROFILE_IMAGE_KEY] = shared;
	} else {
		delete base[PROFILE_IMAGE_KEY];
	}
	return base;
}

/**
 * @param {() => Record<string, unknown>} readData
 * @param {() => "en" | "es"} getLocale
 * @param {number} [delayMs]
 */
export function schedulePersistCvFormData(readData, getLocale, delayMs = 400) {
	clearTimeout(persistTimer);
	persistTimer = window.setTimeout(() => {
		try {
			const L = getLocale();
			if (L !== "en" && L !== "es") return;
			const s = readBilingualStore();
			persistFormIntoStore(s, L, readData);
			writeBilingualStore(s);
		} catch (e) {
			console.warn("CV draft could not be saved (quota or private mode).", e);
		}
	}, delayMs);
}

/**
 * @param {() => Record<string, unknown>} readData
 * @param {"en" | "es"} loc
 */
export function persistLocaleBundleNow(readData, loc) {
	clearTimeout(persistTimer);
	if (loc !== "en" && loc !== "es") return;
	try {
		const s = readBilingualStore();
		persistFormIntoStore(s, loc, readData);
		writeBilingualStore(s);
	} catch (e) {
		console.warn("CV draft could not be saved (quota or private mode).", e);
	}
}
